import { useMutation } from "@apollo/client";
import { UPDATE_EVENT_MUTATION } from "@/pages/events/queries";
import { usePhotoUpload, UploadResult } from "./usePhotoUpload";
import { useSnackbar } from "notistack";
import { useErrorBanner } from "@/providers/ErrorBannerProvider";
import { UploadOptions } from "@/lib/uploadImage";

interface useRecruitmentEventPhotoUpload {
  eventId: string;
  currentPhotoUploads: string[];
  recruitmentData: any;
  onPhotoUploadSuccess: (newPhotoUploads: string[]) => void;
  customUploadOptions?: Partial<UploadOptions>;
}

export const useRecruitmentEventPhotoUpload = ({
  eventId,
  currentPhotoUploads,
  recruitmentData,
  onPhotoUploadSuccess,
  customUploadOptions = {}
}: useRecruitmentEventPhotoUpload) => {
  const { enqueueSnackbar } = useSnackbar();
  const { setError } = useErrorBanner();
  const [updateEventMutation] = useMutation(UPDATE_EVENT_MUTATION);

  // Default options for events, but can be overridden
  const defaultEventOptions: UploadOptions = {
    cloudName: import.meta.env.VITE_CLOUDINARY_CLOUDNAME,
    uploadPreset: import.meta.env.VITE_CLOUDINARY_UPLOAD_PRESET,
    cropping: false,
    sources: ["local", "camera"],
    multiple: true,
    maxFiles: 10, // Allow up to 10 photos at once
    resourceType: "image",
    showSkipCropButton: false,
    folder: "member-images", // Event-specific folder
    showPoweredBy: false,
    clientAllowedFormats: ["png", "jpeg", "jpg", "gif", "webp"]
  };

  // Merge default event options with any custom options
  const finalUploadOptions = { ...defaultEventOptions, ...customUploadOptions };

  const handleUploadSuccess = async (result: UploadResult | UploadResult[]) => {
    if (!eventId) {
      setError("Event ID not found");
      return;
    }

    try {
      // Handle both single and multiple uploads
      const newPhotoUrls = Array.isArray(result) ? result.map((r) => r.secureUrl) : [result.secureUrl];

      // Add the new photos to existing photos
      const allPhotoUploads = [...currentPhotoUploads, ...newPhotoUrls];

      const { data: responseData } = await updateEventMutation({
        variables: {
          input: {
            id: eventId,
            recruitmentEventAttributes: {
              recruitmentEventType: recruitmentData?.recruitmentEventType?.toUpperCase(),
              purpose: recruitmentData?.purpose?.toUpperCase(),
              pgaRepresentation: recruitmentData?.pgaRepresentation?.map((v: string) => v.toUpperCase()),
              supportRequested: recruitmentData?.supportRequested?.map((v: string) => v.toUpperCase()),
              additionalComments: recruitmentData?.additionalComments,
              strategicFunding: recruitmentData?.strategicFunding?.toUpperCase(),
              estimatedExpenses: recruitmentData?.estimatedExpenses,
              sponsorshipAsk: recruitmentData?.sponsorshipAsk,
              estimatedParents: recruitmentData?.estimatedParents,
              estimatedCoaches: recruitmentData?.estimatedCoaches,
              estimatedOtherAttendees: recruitmentData?.estimatedOtherAttendees,
              eventRecap: recruitmentData?.eventRecap,
              overallFeedback: recruitmentData?.overallFeedback,
              eventRating: recruitmentData?.eventRating,
              commentsAndQuotes: recruitmentData?.commentsAndQuotes,
              isFinalized: recruitmentData?.isFinalized,
              photoUploads: allPhotoUploads
            }
          }
        }
      });

      const { success } = responseData.updateEvent;
      if (success) {
        onPhotoUploadSuccess(allPhotoUploads);
        const photoCount = newPhotoUrls.length;
        enqueueSnackbar(
          photoCount === 1 ? "Photo uploaded successfully" : `${photoCount} photos uploaded successfully`
        );
      } else {
        setError(responseData.updateEvent.message || "Failed to upload photos");
      }
    } catch (error) {
      setError("Failed to update event with new photo");
    }
  };

  const handleUploadError = (_error: Error) => {
    setError("Failed to upload photo");
  };

  const { uploadPhoto, isUploading } = usePhotoUpload({
    onUploadSuccess: handleUploadSuccess,
    onUploadError: handleUploadError,
    uploadOptions: finalUploadOptions
  });

  return {
    uploadPhoto,
    isUploading
  };
};
